import React, { useState, useEffect, useRef } from "react";
import EquityCurve from "./EquityCurve";

const C = {
  bg: "#040810", panel: "#0a1220", panel2: "#0d1928", border: "#182b4c",
  text: "#cbd5e1", muted: "#64748b", faint: "#1e3a5f",
  green: "#10b981", red: "#ef4444", blue: "#3b82f6",
  amber: "#f59e0b", purple: "#8b5cf6", cyan: "#06b6d4", orange: "#f97316",
};

const apiFetch = (url, opts = {}) => {
  const token = localStorage.getItem("kara_auth_token");
  return fetch(url, {
    ...opts,
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(opts.headers || {}) },
  });
};

// ── Shared UI atoms ──────────────────────────────────────────────────────────

const Sel = ({ label, value, onChange, children }) => (
  <div style={{ flex: 1, minWidth: 100 }}>
    {label && <div style={{ fontSize: 10, color: C.muted, marginBottom: 4, letterSpacing: 0.5 }}>{label.toUpperCase()}</div>}
    <select value={value} onChange={e => onChange(e.target.value)}
      style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text, padding: "7px 10px", borderRadius: 4, width: "100%", fontSize: 12, outline: "none" }}>
      {children}
    </select>
  </div>
);

const Inp = ({ label, value, onChange, type = "text", min, max }) => (
  <div style={{ flex: 1, minWidth: 85 }}>
    {label && <div style={{ fontSize: 10, color: C.muted, marginBottom: 4, letterSpacing: 0.5 }}>{label.toUpperCase()}</div>}
    <input type={type} value={value} min={min} max={max} onChange={e => onChange(e.target.value)}
      style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text, padding: "7px 10px", borderRadius: 4, width: "100%", fontSize: 12, outline: "none", boxSizing: "border-box" }} />
  </div>
);

const Btn = ({ children, onClick, disabled, color = C.blue, style = {} }) => (
  <button onClick={onClick} disabled={disabled}
    style={{ padding: "10px 18px", background: color, color: "#fff", border: "none", borderRadius: 6, cursor: disabled ? "not-allowed" : "pointer", fontWeight: 700, fontSize: 12, opacity: disabled ? 0.5 : 1, transition: "all 0.2s", ...style }}>
    {children}
  </button>
);

const Badge = ({ label, color }) => (
  <span style={{ background: `${color}22`, color, border: `1px solid ${color}44`, borderRadius: 3, padding: "2px 8px", fontSize: 10, fontWeight: 700, display: "inline-block" }}>{label}</span>
);

function StatCard({ label, value, sub, color, size = "md" }) {
  const fs = size === "lg" ? 24 : size === "sm" ? 15 : 18;
  return (
    <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: size === "lg" ? "14px 16px" : "10px 12px" }}>
      <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: fs, fontWeight: 900, color: color || C.text, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function ProgressTracker({ step, logs, elapsed, mode }) {
  const steps = [
    "Initializing Engine",
    "Fetching Historical Data",
    "Vectorized Indicator Calculations",
    "Simulating Trade Execution & Slippage",
    "Generating Analytical Metrics & Report"
  ];
  return (
    <div style={{ background: C.panel, borderRadius: 12, border: `1px solid ${C.purple}44`, padding: 24, marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: C.purple, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: C.purple, animation: "pulse 1s infinite" }}></span>
          RUNNING ENGINE SIMULATION ({mode.toUpperCase()})
        </div>
        <div style={{ fontSize: 12, color: C.muted, fontFamily: "monospace" }}>Elapsed: {elapsed}s</div>
      </div>
      <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
        {steps.map((st, i) => (
          <div key={i} style={{ flex: 1, height: 6, background: i < step ? C.purple : i === step ? C.blue : C.faint, borderRadius: 3, transition: "background 0.3s" }} />
        ))}
      </div>
      <div style={{ fontSize: 11, color: C.text, fontWeight: 700, marginBottom: 8 }}>Current Phase: <span style={{ color: C.cyan }}>{steps[Math.min(step, steps.length - 1)]}</span></div>
      <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: 10, height: 80, overflowY: "auto", fontFamily: "monospace", fontSize: 10, color: C.muted }}>
        {logs.map((l, i) => <div key={i} style={{ color: i === logs.length - 1 ? C.green : C.muted }}>{l}</div>)}
      </div>
    </div>
  );
}

// ── Trade log ─────────────────────────────────────────────────────────────────

function TradesTable({ trades }) {
  const [filter, setFilter] = useState("ALL");
  const [page, setPage] = useState(0);
  const PER = 30;
  const filtered = (trades || []).filter(t => filter === "ALL" || t.result === filter);
  const paged = filtered.slice(page * PER, (page + 1) * PER);
  const pages = Math.ceil(filtered.length / PER);

  const exportCSV = () => {
    const rows = (trades || []).map(t => `${t.num},${t.direction},${t.entry_time},${t.exit_time},${t.entry},${t.exit},${t.pnl.toFixed(4)},${t.result},${t.bars_held}`);
    const blob = new Blob(["#,Dir,Entry,Exit,Price In,Price Out,PnL,Result,Bars\n" + rows.join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "trades.csv"; a.click();
  };

  const fb = active => ({ padding: "5px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 4, border: `1px solid ${active ? C.blue : C.border}`, background: active ? `${C.blue}22` : "transparent", color: active ? C.blue : C.muted });

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 6 }}>
          {["ALL","WIN","LOSS"].map(f => <button key={f} style={fb(filter === f)} onClick={() => { setFilter(f); setPage(0); }}>{f} {filter===f?`(${filtered.length})`:""}</button>)}
        </div>
        <button onClick={exportCSV} style={{ ...fb(false), color: C.cyan, borderColor: C.cyan }}>↓ Export CSV</button>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
          <thead>
            <tr>{["#","Dir","Entry Time","Exit Time","In","Out","PnL","Result","Bars"].map(h => (
              <th key={h} style={{ padding: "7px 10px", color: C.muted, fontWeight: 700, textAlign: "left", borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap" }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {paged.map(t => (
              <tr key={t.num} style={{ borderBottom: `1px solid ${C.faint}` }}>
                <td style={{ padding: "6px 10px", color: C.muted }}>{t.num}</td>
                <td style={{ padding: "6px 10px" }}><Badge label={t.direction} color={t.direction === "CALL" ? C.green : C.red} /></td>
                <td style={{ padding: "6px 10px", color: C.text, whiteSpace: "nowrap" }}>{t.entry_time}</td>
                <td style={{ padding: "6px 10px", color: C.text, whiteSpace: "nowrap" }}>{t.exit_time}</td>
                <td style={{ padding: "6px 10px", color: C.text }}>{t.entry}</td>
                <td style={{ padding: "6px 10px", color: C.text }}>{t.exit}</td>
                <td style={{ padding: "6px 10px", color: t.pnl >= 0 ? C.green : C.red, fontWeight: 700 }}>{t.pnl >= 0 ? "+" : ""}${t.pnl.toFixed(4)}</td>
                <td style={{ padding: "6px 10px" }}><Badge label={t.result} color={t.result === "WIN" ? C.green : C.red} /></td>
                <td style={{ padding: "6px 10px", color: C.muted }}>{t.bars_held}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 10 }}>
          <button style={fb(false)} disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Prev</button>
          <span style={{ fontSize: 11, color: C.muted, alignSelf: "center" }}>{page + 1} / {pages}</span>
          <button style={fb(false)} disabled={page >= pages - 1} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}
    </div>
  );
}

// ── Monthly table ─────────────────────────────────────────────────────────────

function MonthlyTable({ monthly }) {
  if (!monthly || !Object.keys(monthly).length) return <div style={{ color: C.muted, fontSize: 12, padding: 20 }}>No monthly data</div>;
  const entries = Object.entries(monthly).sort(([a], [b]) => a.localeCompare(b));
  const maxAbs = Math.max(...entries.map(([, v]) => Math.abs(v))) || 1;
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
      <thead><tr>{["Month","PnL","Bar"].map(h => (
        <th key={h} style={{ padding: "7px 12px", color: C.muted, fontWeight: 700, textAlign: "left", borderBottom: `1px solid ${C.border}` }}>{h}</th>
      ))}</tr></thead>
      <tbody>{entries.map(([m, pnl]) => (
        <tr key={m} style={{ borderBottom: `1px solid ${C.faint}` }}>
          <td style={{ padding: "7px 12px", color: C.text }}>{m.slice(0, 7)}</td>
          <td style={{ padding: "7px 12px", color: pnl >= 0 ? C.green : C.red, fontWeight: 700 }}>{pnl >= 0 ? "+" : ""}${pnl.toFixed(2)}</td>
          <td style={{ padding: "7px 12px", width: "50%" }}>
            <div style={{ height: 8, borderRadius: 3, width: `${Math.round(Math.abs(pnl) / maxAbs * 100)}%`, background: pnl >= 0 ? C.green : C.red, opacity: 0.7, minWidth: 2 }} />
          </td>
        </tr>
      ))}</tbody>
    </table>
  );
}

// ── WFA results ───────────────────────────────────────────────────────────────

function WFAResults({ data }) {
  const s = data.summary;
  const verdictColor = s.verdict === "ROBUST" ? C.green : s.verdict === "MARGINAL" ? C.amber : C.red;
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10, marginBottom: 20 }}>
        <StatCard label="Verdict" value={s.verdict} color={verdictColor} size="lg" />
        <StatCard label="Robustness Score" value={s.robustness_score.toFixed(3)} color={verdictColor} size="lg" />
        <StatCard label="Avg OOS Win Rate" value={(s.avg_oos_win_rate * 100).toFixed(1) + "%"} color={s.avg_oos_win_rate >= 0.55 ? C.green : C.red} />
        <StatCard label="Avg OOS Net PnL" value={(s.avg_oos_net_profit >= 0 ? "+" : "") + "$" + s.avg_oos_net_profit.toFixed(2)} color={s.avg_oos_net_profit >= 0 ? C.green : C.red} />
        <StatCard label="WR Degradation" value={((s.avg_wr_degradation) * 100).toFixed(1) + "%"} color={s.avg_wr_degradation < 0.05 ? C.green : s.avg_wr_degradation < 0.15 ? C.amber : C.red} sub="IS → OOS" />
        <StatCard label="Robust Folds" value={`${Math.round(s.robust_folds_pct * data.n_splits)}/${data.n_splits}`} color={s.robust_folds_pct >= 0.6 ? C.green : C.amber} />
      </div>

      <div style={{ background: C.bg, borderRadius: 8, border: `1px solid ${C.border}`, padding: 4, marginBottom: 20, overflow: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
          <thead><tr style={{ borderBottom: `1px solid ${C.border}` }}>
            {["Fold","IS Bars","OOS Bars","IS WR","OOS WR","WR Δ","OOS Profit","OOS Max DD","OOS PF","Robust"].map(h => (
              <th key={h} style={{ padding: "8px 10px", color: C.muted, fontWeight: 700, textAlign: "left", whiteSpace: "nowrap" }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>{data.folds.map(f => (
            <tr key={f.fold} style={{ borderBottom: `1px solid ${C.faint}` }}>
              <td style={{ padding: "7px 10px", color: C.blue, fontWeight: 700 }}>#{f.fold}</td>
              <td style={{ padding: "7px 10px", color: C.muted }}>{f.is_bars}</td>
              <td style={{ padding: "7px 10px", color: C.muted }}>{f.oos_bars}</td>
              <td style={{ padding: "7px 10px", color: C.green }}>{(f.is_metrics.win_rate * 100).toFixed(1)}%</td>
              <td style={{ padding: "7px 10px", color: f.oos_metrics.win_rate >= 0.5 ? C.green : C.red, fontWeight: 700 }}>{(f.oos_metrics.win_rate * 100).toFixed(1)}%</td>
              <td style={{ padding: "7px 10px", color: f.wr_degradation > 0.1 ? C.red : C.amber }}>{(f.wr_degradation * 100).toFixed(1)}%</td>
              <td style={{ padding: "7px 10px", color: f.oos_metrics.net_profit >= 0 ? C.green : C.red, fontWeight: 700 }}>{f.oos_metrics.net_profit >= 0 ? "+" : ""}${f.oos_metrics.net_profit.toFixed(2)}</td>
              <td style={{ padding: "7px 10px", color: f.oos_metrics.max_drawdown > 0.15 ? C.red : C.amber }}>{(f.oos_metrics.max_drawdown * 100).toFixed(1)}%</td>
              <td style={{ padding: "7px 10px", color: C.text }}>{f.oos_metrics.profit_factor?.toFixed(2)}</td>
              <td style={{ padding: "7px 10px" }}><Badge label={f.robust ? "YES" : "NO"} color={f.robust ? C.green : C.red} /></td>
            </tr>
          ))}</tbody>
        </table>
      </div>

      <div style={{ background: C.bg, borderRadius: 8, border: `1px solid ${C.border}`, padding: "12px 16px" }}>
        <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, marginBottom: 10 }}>BEST PARAMS BY FOLD</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {data.folds.map(f => (
            <div key={f.fold} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, color: C.blue, fontWeight: 700, minWidth: 50 }}>Fold #{f.fold}</span>
              {Object.entries(f.best_params || {}).map(([k, v]) => (
                <span key={k} style={{ background: `${C.purple}15`, color: C.purple, border: `1px solid ${C.purple}33`, padding: "2px 8px", borderRadius: 3, fontSize: 10 }}>
                  {k}: <strong style={{ color: "#fff" }}>{typeof v === "number" ? v.toFixed(3) : v}</strong>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Monte Carlo results ────────────────────────────────────────────────────────

function MonteCarloResults({ data }) {
  const rd = data.return_distribution;
  const dd = data.drawdown_distribution;
  const verdictColor = data.verdict === "ROBUST" ? C.green : data.verdict === "MARGINAL" ? C.amber : C.red;
  const probPct = (data.prob_profitable * 100).toFixed(1);

  const BarChart = ({ dist, label, pct, color }) => {
    const bars = [
      { l: "5th %ile", v: dist.p5 },
      { l: "25th %ile", v: dist.p25 || dist.p50 },
      { l: "Median", v: dist.p50 },
      { l: "75th %ile", v: dist.p75 || dist.p50 },
      { l: "95th %ile", v: dist.p95 },
    ].filter(b => b.v !== undefined);
    const max = Math.max(...bars.map(b => Math.abs(b.v)));
    return (
      <div>
        <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, marginBottom: 10 }}>{label}</div>
        {bars.map(b => (
          <div key={b.l} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <div style={{ width: 70, fontSize: 10, color: C.muted, textAlign: "right" }}>{b.l}</div>
            <div style={{ flex: 1, height: 14, background: C.faint, borderRadius: 3, position: "relative" }}>
              <div style={{ position: "absolute", left: 0, top: 0, height: "100%", borderRadius: 3, width: `${Math.round(Math.abs(b.v) / (max || 1) * 100)}%`, background: b.v >= 0 ? color : C.red, opacity: 0.75 }} />
            </div>
            <div style={{ width: 60, fontSize: 10, color: b.v >= 0 ? color : C.red, fontWeight: 700, textAlign: "right" }}>{pct ? (b.v * 100).toFixed(1) + "%" : "$" + b.v.toFixed(2)}</div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10, marginBottom: 20 }}>
        <StatCard label="Verdict" value={data.verdict} color={verdictColor} size="lg" />
        <StatCard label="Prob. Profitable" value={probPct + "%"} color={data.prob_profitable >= 0.7 ? C.green : data.prob_profitable >= 0.5 ? C.amber : C.red} size="lg" sub={`${data.n_simulations} simulations`} />
        <StatCard label="Base Win Rate" value={(data.base_result.win_rate * 100).toFixed(1) + "%"} color={C.blue} />
        <StatCard label="Base Net Profit" value={(data.base_result.net_profit >= 0 ? "+" : "") + "$" + data.base_result.net_profit.toFixed(2)} color={data.base_result.net_profit >= 0 ? C.green : C.red} />
        <StatCard label="Median Return" value={(rd.p50 >= 0 ? "+" : "") + (rd.p50 * 100).toFixed(1) + "%"} color={rd.p50 >= 0 ? C.green : C.red} />
        <StatCard label="Worst DD (95th)" value={(dd.p95 * 100).toFixed(1) + "%"} color={dd.p95 > 0.2 ? C.red : C.amber} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
        <div style={{ background: C.bg, borderRadius: 8, border: `1px solid ${C.border}`, padding: "14px 16px" }}>
          <BarChart dist={rd} label="RETURN DISTRIBUTION" color={C.green} pct={true} />
        </div>
        <div style={{ background: C.bg, borderRadius: 8, border: `1px solid ${C.border}`, padding: "14px 16px" }}>
          <BarChart dist={dd} label="MAX DRAWDOWN DISTRIBUTION" color={C.amber} pct={true} />
        </div>
      </div>

      <div style={{ background: `${verdictColor}11`, border: `1px solid ${verdictColor}33`, borderRadius: 8, padding: "14px 18px", fontSize: 12 }}>
        <strong style={{ color: verdictColor }}>{data.verdict}</strong>
        {data.verdict === "ROBUST" && " — Strategy shows consistent edge regardless of trade order. High probability of real-world profitability."}
        {data.verdict === "MARGINAL" && " — Results are somewhat sensitive to trade order. Strategy may have some edge but needs more data or tighter parameters."}
        {data.verdict === "FRAGILE" && " — Returns are highly dependent on specific trade sequence. Results may be luck. Consider optimizing further or changing strategy."}
      </div>
    </div>
  );
}

// ── Compare results ───────────────────────────────────────────────────────────

function CompareResults({ data }) {
  const keys = [
    ["win_rate", "Win Rate", v => (v * 100).toFixed(1) + "%", true],
    ["net_profit", "Net Profit", v => (v >= 0 ? "+" : "") + "$" + v.toFixed(2), true],
    ["profit_factor", "Profit Factor", v => v.toFixed(2) + "x", true],
    ["max_drawdown", "Max Drawdown", v => (v * 100).toFixed(1) + "%", false],
    ["sharpe_ratio", "Sharpe", v => v.toFixed(3), true],
    ["sortino_ratio", "Sortino", v => v.toFixed(3), true],
    ["calmar_ratio", "Calmar", v => v.toFixed(3), true],
    ["sqn", "SQN", v => v.toFixed(3), true],
    ["recovery_factor", "Recovery Factor", v => v.toFixed(2) + "x", true],
    ["total_trades", "Total Trades", v => String(v), true],
    ["profitable_weeks_pct", "Profitable Weeks", v => (v * 100).toFixed(1) + "%", true],
    ["expectancy", "Expectancy/Trade", v => (v >= 0 ? "+" : "") + "$" + v.toFixed(4), true],
  ];

  const wa = data.score_a, wb = data.score_b;
  const nameA = data.strategy_a.name?.replace(".md", "") || "Strategy A";
  const nameB = data.strategy_b.name?.replace(".md", "") || "Strategy B";

  return (
    <div>
      <div style={{ display: "flex", gap: 16, marginBottom: 20, alignItems: "center" }}>
        <div style={{ flex: 1, textAlign: "center", padding: "14px", background: wa > wb ? `${C.green}15` : C.bg, border: `2px solid ${wa > wb ? C.green : C.border}`, borderRadius: 8 }}>
          <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>STRATEGY A</div>
          <div style={{ fontSize: 14, fontWeight: 900, color: wa > wb ? C.green : C.text }}>{nameA}</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: wa > wb ? C.green : C.text, marginTop: 4 }}>{wa} pts</div>
        </div>
        <div style={{ fontSize: 20, color: C.muted, fontWeight: 900 }}>VS</div>
        <div style={{ flex: 1, textAlign: "center", padding: "14px", background: wb > wa ? `${C.green}15` : C.bg, border: `2px solid ${wb > wa ? C.green : C.border}`, borderRadius: 8 }}>
          <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>STRATEGY B</div>
          <div style={{ fontSize: 14, fontWeight: 900, color: wb > wa ? C.green : C.text }}>{nameB}</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: wb > wa ? C.green : C.text, marginTop: 4 }}>{wb} pts</div>
        </div>
      </div>

      <div style={{ overflowX: "auto", marginBottom: 20 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
          <thead><tr style={{ borderBottom: `1px solid ${C.border}` }}>
            {["Metric", nameA, nameB, "Winner"].map(h => (
              <th key={h} style={{ padding: "8px 12px", color: C.muted, fontWeight: 700, textAlign: "left" }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {keys.map(([key, label, fmt, hib]) => {
              const va = data.metrics_a[key], vb = data.metrics_b[key];
              const w = data.comparison[key];
              return (
                <tr key={key} style={{ borderBottom: `1px solid ${C.faint}` }}>
                  <td style={{ padding: "7px 12px", color: C.muted }}>{label}</td>
                  <td style={{ padding: "7px 12px", color: w === "a" ? C.green : C.text, fontWeight: w === "a" ? 700 : 400 }}>{va != null ? fmt(va) : "—"}</td>
                  <td style={{ padding: "7px 12px", color: w === "b" ? C.green : C.text, fontWeight: w === "b" ? 700 : 400 }}>{vb != null ? fmt(vb) : "—"}</td>
                  <td style={{ padding: "7px 12px" }}>
                    {w === "tie" ? <Badge label="TIE" color={C.muted} /> : <Badge label={w === "a" ? "A ✓" : "B ✓"} color={C.green} />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Strategy Gallery Modal/Drawer ──────────────────────────────────────────────

function StrategyGallery({ strategies, onSelect, onClose, liveStats }) {
  const [search, setSearch] = useState("");
  const filtered = (strategies || []).filter(s => s.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 9999, display: "flex", justifyContent: "center", alignItems: "center", padding: 20 }}>
      <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, width: "100%", maxWidth: 850, maxHeight: "85vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 900, color: "#fff" }}>Strategy Library & Knowledge Base</div>
            <div style={{ fontSize: 11, color: C.muted }}>Select a strategy to backtest or view live performance metrics</div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: C.muted, fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ padding: "12px 20px", borderBottom: `1px solid ${C.border}`, background: C.bg }}>
          <input type="text" placeholder="Search strategies (e.g. 90_STRATEGY, URUS, Sweep)..." value={search} onChange={e => setSearch(e.target.value)}
            style={{ width: "100%", background: C.panel, border: `1px solid ${C.border}`, color: C.text, padding: "8px 12px", borderRadius: 6, fontSize: 12, outline: "none", boxSizing: "border-box" }} />
        </div>
        <div style={{ padding: 20, overflowY: "auto", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 14 }}>
          {filtered.map(s => {
            const cleanName = s.name.replace(".md", "");
            const stats = liveStats[cleanName] || {};
            return (
              <div key={s.name} onClick={() => { onSelect(s.name); onClose(); }}
                style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: 14, cursor: "pointer", transition: "all 0.2s", ":hover": { borderColor: C.blue } }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: C.cyan, marginBottom: 6 }}>{cleanName}</div>
                <div style={{ fontSize: 10, color: C.muted, height: 32, overflow: "hidden", textOverflow: "ellipsis", marginBottom: 10 }}>
                  {s.description || "Deterministic trading engine strategy designed for synthetic market edge."}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 10, borderTop: `1px solid ${C.faint}`, paddingTop: 8 }}>
                  <span style={{ color: C.muted }}>Live Trades: <strong style={{ color: C.text }}>{stats.count || 0}</strong></span>
                  <Badge label={stats.wr ? `${(stats.wr * 100).toFixed(1)}% Live WR` : "Untested"} color={stats.wr >= 0.55 ? C.green : C.amber} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const AVAILABLE_SYMBOLS = [
  {
    group: "Volatility Synthetics",
    items: [
      { value: "R_50", label: "R_50 — Volatility 50" },
      { value: "R_25", label: "R_25 — Volatility 25" },
      { value: "R_75", label: "R_75 — Volatility 75" },
      { value: "R_100", label: "R_100 — Volatility 100" },
      { value: "R_10", label: "R_10 — Volatility 10" },
      { value: "1HZ10V", label: "1HZ10V — Volatility 10 (1s)" },
      { value: "1HZ25V", label: "1HZ25V — Volatility 25 (1s)" },
      { value: "1HZ50V", label: "1HZ50V — Volatility 50 (1s)" },
      { value: "1HZ75V", label: "1HZ75V — Volatility 75 (1s)" },
      { value: "1HZ100V", label: "1HZ100V — Volatility 100 (1s)" },
    ]
  },
  {
    group: "Boom & Crash",
    items: [
      { value: "BOOM500", label: "BOOM500 — Boom 500" },
      { value: "BOOM1000", label: "BOOM1000 — Boom 1000" },
      { value: "CRASH500", label: "CRASH500 — Crash 500" },
      { value: "CRASH1000", label: "CRASH1000 — Crash 1000" },
    ]
  },
  {
    group: "Jump & Step",
    items: [
      { value: "JD10", label: "JD10 — Jump 10" },
      { value: "JD25", label: "JD25 — Jump 25" },
      { value: "JD50", label: "JD50 — Jump 50" },
      { value: "JD75", label: "JD75 — Jump 75" },
      { value: "JD100", label: "JD100 — Jump 100" },
      { value: "STEP", label: "STEP — Step Index" },
    ]
  },
  {
    group: "Forex & Commodities",
    items: [
      { value: "frxEURUSD", label: "EUR/USD — Euro / Dollar" },
      { value: "frxGBPUSD", label: "GBP/USD — Cable" },
      { value: "frxUSDJPY", label: "USD/JPY — Dollar / Yen" },
      { value: "frxXAUUSD", label: "XAU/USD — Gold" },
    ]
  }
];

export default function AdvancedBacktestTab({ strategies, activeStrategy }) {
  const [symbol, setSymbol]       = useState("R_50");
  const [isCustomSymbol, setIsCustomSymbol] = useState(false);
  const [tf, setTf]               = useState("1m");
  const [strat, setStrat]         = useState(activeStrategy || "90_STRATEGY.md");
  const [stratB, setStratB]       = useState("");
  const [bars, setBars]           = useState(1200);
  const [capital, setCapital]     = useState(1000);
  const [risk, setRisk]           = useState(0.02);
  const [slippage, setSlippage]   = useState(0.0005);
  const [showAdv, setShowAdv]     = useState(false);
  const [nTrials, setNTrials]     = useState(75);
  const [wfaSplits, setWfaSplits] = useState(5);
  const [wfaTrain, setWfaTrain]   = useState(70);
  const [mcSims, setMcSims]       = useState(1000);
  const [mode, setMode]           = useState("backtest");
  const [loading, setLoading]     = useState(false);
  const [loadStep, setLoadStep]   = useState(0);
  const [loadLogs, setLoadLogs]   = useState([]);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [results, setResults]     = useState({});
  const [error, setError]         = useState(null);
  const [viewTab, setViewTab]     = useState("overview");
  const [sidecarParams, setSidecarParams] = useState(null);
  const [rewardRatio, setRewardRatio] = useState(5);
  const [targetWr, setTargetWr] = useState(0.35);
  const [minTpd, setMinTpd] = useState(10);
  const [showGallery, setShowGallery] = useState(false);
  const [liveWRs, setLiveWRs]     = useState({});

  // Lab priorities
  const [showLab, setShowLab]           = useState(false);
  const [allLabStrats, setAllLabStrats] = useState([]);
  const [labPrios, setLabPrios]         = useState([]);
  const [labSave, setLabSave]           = useState(null);

  const timerRef = useRef(null);

  useEffect(() => {
    apiFetch("/api/lab/priority-strategies").then(r => r.ok ? r.json() : null)
      .then(d => { if (d) { setAllLabStrats(d.all_strategies || []); setLabPrios(d.priority_strategies || []); } }).catch(() => {});
    
    // Fetch live WR stats from trade store
    apiFetch(`/api/analytics/live-wr?symbol=${symbol}`).then(r => r.ok ? r.json() : null)
      .then(d => { if (d && d.stats) setLiveWRs(d.stats); }).catch(() => {});
  }, [symbol]);

  const startProgress = () => {
    setLoadStep(0);
    setLoadLogs(["[0.0s] Initializing Backtester environment..."]);
    setElapsedSec(0);
    const start = Date.now();
    timerRef.current = setInterval(() => {
      const sec = ((Date.now() - start) / 1000).toFixed(1);
      setElapsedSec(sec);
      if (sec > 1.5 && loadStep < 1) {
        setLoadStep(1);
        setLoadLogs(l => [...l, `[${sec}s] Connecting to Deriv Websocket / local candle cache...`]);
      } else if (sec > 3.0 && loadStep < 2) {
        setLoadStep(2);
        setLoadLogs(l => [...l, `[${sec}s] Data loaded. Computing indicators & signals (Wilder RMA, TMA, HAS)...`]);
      } else if (sec > 5.0 && loadStep < 3) {
        setLoadStep(3);
        setLoadLogs(l => [...l, `[${sec}s] Executing next-bar trades with 1:${rewardRatio} R:R math & payout rules...`]);
      }
    }, 200);
  };

  const stopProgress = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setLoadStep(4);
    setLoadLogs(l => [...l, `Execution finished cleanly.`]);
  };

  const rrGoal = () => ({
    reward_ratio: parseFloat(rewardRatio) || 2,
    target_win_rate: parseFloat(targetWr) || ((parseFloat(rewardRatio) || 2) >= 5 ? 0.35 : 0.55),
    min_trades_per_day: parseFloat(minTpd) || 0,
  });

  const setRRProfile = (rr) => {
    const n = parseFloat(rr);
    setRewardRatio(n);
    setTargetWr(n >= 5 ? 0.35 : 0.55);
  };

  const getOptions = () => ({
    initial_capital: parseFloat(capital),
    risk_per_trade: parseFloat(risk),
    slippage_pct: parseFloat(slippage),
    ...rrGoal(),
    min_win_rate: rrGoal().target_win_rate,
  });

  const fetchCandles = async () => {
    const r = await apiFetch(`/api/chart-history?sym=${encodeURIComponent(symbol)}&tf=${encodeURIComponent(tf)}&count=${bars}`);
    if (!r.ok) throw new Error(`Candle fetch failed (${r.status})`);
    const d = await r.json();
    if (!d.candles || d.candles.length < 50) throw new Error("Not enough candles — try a different symbol/timeframe");
    return d.candles;
  };

  const runAction = async () => {
    setLoading(true); setError(null); startProgress();
    try {
      const candles = await fetchCandles();

      if (mode === "backtest") {
        const r = await apiFetch("/api/advanced-backtest/run", { method: "POST", body: JSON.stringify({ candles, strategy: strat, params: {}, options: getOptions() }) });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "Backtest failed");
        setSidecarParams(d.resolved_params);
        setResults(p => ({ ...p, backtest: d }));
        setViewTab("overview");
      }

      if (mode === "optimize") {
        const r = await apiFetch("/api/advanced-backtest/optimize", { method: "POST", body: JSON.stringify({ candles, strategy: strat, param_ranges: {}, options: getOptions(), n_trials: parseInt(nTrials) }) });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "Optimization failed");
        setSidecarParams(d.resolved_params);
        setResults(p => ({ ...p, optimize: d }));
        setViewTab("overview");
      }

      if (mode === "wfa") {
        const r = await apiFetch("/api/advanced-backtest/wfa", { method: "POST", body: JSON.stringify({ candles, strategy: strat, param_ranges: {}, options: getOptions(), n_splits: parseInt(wfaSplits), train_pct: parseInt(wfaTrain) / 100, n_trials: parseInt(nTrials) }) });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "WFA failed");
        setResults(p => ({ ...p, wfa: d }));
        setViewTab("wfa");
      }

      if (mode === "montecarlo") {
        const r = await apiFetch("/api/advanced-backtest/montecarlo", { method: "POST", body: JSON.stringify({ candles, strategy: strat, params: {}, options: getOptions(), n_simulations: parseInt(mcSims) }) });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "Monte Carlo failed");
        setResults(p => ({ ...p, montecarlo: d }));
        setViewTab("montecarlo");
      }

      if (mode === "compare") {
        const r = await apiFetch("/api/advanced-backtest/compare", { method: "POST", body: JSON.stringify({ candles, strategy_a: strat, strategy_b: stratB, params_a: {}, params_b: {}, options: getOptions() }) });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "Compare failed");
        setResults(p => ({ ...p, compare: d }));
        setViewTab("compare");
      }
    } catch (e) {
      setError(e.message);
    } finally {
      stopProgress();
      setLoading(false);
    }
  };

  const modeBtn = (id, label, color) => (
    <button key={id} onClick={() => setMode(id)} style={{
      flex: 1, padding: "8px 6px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 6,
      border: `1px solid ${mode === id ? color : C.border}`,
      background: mode === id ? `${color}22` : "transparent",
      color: mode === id ? color : C.muted,
      transition: "all 0.2s"
    }}>{label}</button>
  );

  const activeResult = results[mode];
  const m = activeResult?.best_metrics || activeResult?.metrics;
  const wrColor = m ? (m.win_rate >= targetWr ? C.green : C.red) : C.text;

  const tabBtn = (id, label, disabled) => (
    <button key={id} disabled={disabled} onClick={() => setViewTab(id)} style={{
      padding: "9px 16px", fontSize: 11, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer",
      border: "none", borderBottom: `2px solid ${viewTab === id ? C.blue : "transparent"}`,
      background: "transparent", color: disabled ? C.faint : viewTab === id ? C.blue : C.muted,
    }}>{label}</button>
  );

  const hasOverview = !!(results.backtest || results.optimize);

  return (
    <div style={{ padding: "20px 24px", color: C.text, fontFamily: "sans-serif", maxWidth: 1550, margin: "0 auto" }}>
      
      {showGallery && <StrategyGallery strategies={strategies} liveStats={liveWRs} onSelect={setStrat} onClose={() => setShowGallery(false)} />}

      {/* Header */}
      <div style={{ marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
        <div style={{ borderLeft: `4px solid ${C.blue}`, paddingLeft: 14 }}>
          <div style={{ fontSize: 22, fontWeight: 900, color: "#fff", letterSpacing: -0.5 }}>QUANT LAB & BACKTEST SUITE</div>
          <div style={{ fontSize: 12, color: C.muted }}>Deterministic execution engine with 1:5 / 1:2 R:R optimization & live trade store reconciliation</div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={() => setShowGallery(true)} style={{ padding: "8px 14px", background: `${C.cyan}15`, border: `1px solid ${C.cyan}44`, color: C.cyan, borderRadius: 6, fontSize: 11, fontWeight: 800, cursor: "pointer" }}>
            📚 STRATEGY GALLERY
          </button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>

        {/* ── LEFT CONTROL PANEL ─────────────────────────────────────────── */}
        <div style={{ width: 310, minWidth: 290, flexShrink: 0, display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Mode Selector */}
          <div style={{ background: C.panel, padding: 14, borderRadius: 10, border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, marginBottom: 10, letterSpacing: 0.5 }}>SIMULATION MODE</div>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {modeBtn("backtest",   "▶ Backtest", C.blue)}
              {modeBtn("optimize",   "🧬 Optimize", C.purple)}
              {modeBtn("wfa",        "📐 WFA",     C.cyan)}
              {modeBtn("montecarlo", "🎲 Monte Carlo", C.amber)}
              {modeBtn("compare",    "⚡ Compare",  C.orange)}
            </div>
          </div>

          {/* Config */}
          <div style={{ background: C.panel, padding: 16, borderRadius: 10, border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, marginBottom: 12, letterSpacing: 0.5 }}>TARGET & MARKET</div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10, color: C.muted, marginBottom: 4, letterSpacing: 0.5 }}>TARGET SYMBOL</div>
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ flex: 1, minWidth: 140 }}>
                  <select
                    value={isCustomSymbol ? "CUSTOM" : symbol}
                    onChange={e => {
                      const v = e.target.value;
                      if (v === "CUSTOM") {
                        setIsCustomSymbol(true);
                      } else {
                        setIsCustomSymbol(false);
                        setSymbol(v);
                      }
                    }}
                    style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text, padding: "7px 10px", borderRadius: 4, width: "100%", fontSize: 12, outline: "none", boxSizing: "border-box" }}
                  >
                    {AVAILABLE_SYMBOLS.map(grp => (
                      <optgroup key={grp.group} label={grp.group}>
                        {grp.items.map(item => (
                          <option key={item.value} value={item.value}>{item.label}</option>
                        ))}
                      </optgroup>
                    ))}
                    <option value="CUSTOM">✏️ Custom Symbol...</option>
                  </select>
                </div>
                <Sel label="Timeframe" value={tf} onChange={setTf}>
                  {["1m","5m","15m","30m","1h","4h","1d"].map(t => <option key={t} value={t}>{t}</option>)}
                </Sel>
              </div>
              {isCustomSymbol && (
                <div style={{ marginTop: 6 }}>
                  <input
                    type="text"
                    placeholder="Enter custom symbol (e.g. BTCUSD, R_50)"
                    value={symbol}
                    onChange={e => setSymbol(e.target.value.toUpperCase())}
                    style={{ background: C.bg, border: `1px solid ${C.blue}`, color: C.text, padding: "7px 10px", borderRadius: 4, width: "100%", fontSize: 12, outline: "none", boxSizing: "border-box" }}
                  />
                </div>
              )}
            </div>
            
            <div style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <span style={{ fontSize: 10, color: C.muted, fontWeight: 700 }}>STRATEGY</span>
                <span onClick={() => setShowGallery(true)} style={{ fontSize: 10, color: C.blue, cursor: "pointer" }}>Browse All →</span>
              </div>
              <Sel value={strat} onChange={setStrat}>
                <option value="">-- Select Strategy --</option>
                {(strategies || []).map(s => <option key={s.name} value={s.name}>{s.name.replace(".md","")}</option>)}
              </Sel>
            </div>

            {mode === "compare" && (
              <div style={{ marginBottom: 10 }}>
                <Sel label="Strategy B" value={stratB} onChange={setStratB}>
                  <option value="">-- Select Opponent --</option>
                  {(strategies || []).map(s => <option key={s.name} value={s.name}>{s.name.replace(".md","")}</option>)}
                </Sel>
              </div>
            )}

            <Sel label="Data Window" value={String(bars)} onChange={v => setBars(parseInt(v))}>
              <option value="600">600 bars (~10 hrs on M1)</option>
              <option value="1200">1,200 bars (~1 day on M1)</option>
              <option value="43200">43,200 bars (~30 days on M1)</option>
            </Sel>

            <button onClick={() => setShowAdv(v => !v)} style={{ background: "transparent", border: "none", color: C.blue, fontSize: 11, cursor: "pointer", padding: "8px 0 0 0", fontWeight: 700 }}>
              {showAdv ? "▼ Hide Execution Options" : "▶ Account & Risk Options"}
            </button>
            {showAdv && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
                <Inp label="Capital ($)" type="number" value={capital} onChange={setCapital} />
                <Inp label="Risk/Trade (0.02=2%)" type="number" value={risk} onChange={setRisk} />
                <Inp label="Slippage" type="number" value={slippage} onChange={setSlippage} />
              </div>
            )}
          </div>

          {/* R:R Mission Target */}
          <div style={{ background: `linear-gradient(135deg, ${C.panel}, ${C.panel2})`, padding: 14, borderRadius: 12, border: `1px solid ${C.cyan}44` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 900, color: C.cyan }}>🎯 PROFITABILITY TARGET</div>
              <Badge label={`1:${rewardRatio} R:R`} color={C.green} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
              <button onClick={() => setRRProfile(5)} style={{ padding: "8px", borderRadius: 6, border: `1px solid ${Number(rewardRatio) === 5 ? C.green : C.border}`, background: Number(rewardRatio) === 5 ? `${C.green}22` : C.bg, color: Number(rewardRatio) === 5 ? C.green : C.text, cursor: "pointer", fontWeight: 800, fontSize: 11 }}>1:5 · 35%+ WR</button>
              <button onClick={() => setRRProfile(2)} style={{ padding: "8px", borderRadius: 6, border: `1px solid ${Number(rewardRatio) === 2 ? C.blue : C.border}`, background: Number(rewardRatio) === 2 ? `${C.blue}22` : C.bg, color: Number(rewardRatio) === 2 ? C.blue : C.text, cursor: "pointer", fontWeight: 800, fontSize: 11 }}>1:2 · 55%+ WR</button>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <Inp label="Reward Ratio" type="number" value={rewardRatio} onChange={setRewardRatio} />
              <Inp label="Target WR" type="number" value={targetWr} onChange={setTargetWr} />
              <Inp label="Min Trades/Day" type="number" value={minTpd} onChange={setMinTpd} />
            </div>
          </div>

          {/* Action Button */}
          <Btn onClick={runAction} disabled={loading || !strat || (mode === "compare" && !stratB)}
            color={mode === "optimize" ? C.purple : mode === "wfa" ? C.cyan : mode === "montecarlo" ? C.amber : mode === "compare" ? C.orange : C.blue}
            style={{ width: "100%", padding: "12px", fontSize: 13, boxShadow: "0 4px 12px rgba(0,0,0,0.3)" }}>
            {loading ? "EXECUTING..." : mode === "backtest" ? "▶ RUN BACKTEST" : mode === "optimize" ? "🧬 FIND OPTIMAL PARAMS" : mode === "wfa" ? "📐 RUN WALK-FORWARD" : mode === "montecarlo" ? "🎲 RUN MONTE CARLO" : "⚡ COMPARE STRATEGIES"}
          </Btn>

          {/* Auto Strategy Improver */}
          <div style={{ background: C.panel, padding: 14, borderRadius: 12, border: `1px solid ${C.purple}44` }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: C.purple, marginBottom: 4 }}>⚡ AUTO-STRATEGY IMPROVER</div>
            <div style={{ fontSize: 10, color: C.muted, marginBottom: 8 }}>Triggers full 500-day candle optimization with trade store reconciliation.</div>
            <Btn onClick={async () => {
              const goal = rrGoal();
              setLoading(true); startProgress(); setError(null);
              try {
                const r = await apiFetch("/api/strategy/generate", {
                  method: "POST",
                  body: JSON.stringify({
                    symbol, granularity: 60, db_count: 43200,
                    target_wr: goal.target_win_rate, min_trades: 25, min_trades_per_day: goal.min_trades_per_day, reward_ratio: goal.reward_ratio,
                    n_trials: 100, engine: "URUS_88", strategy_name: `${(strat || "90_STRATEGY").replace(".md", "")}_RR${String(goal.reward_ratio).replace(".", "_")}`
                  })
                });
                const d = await r.json();
                if (!r.ok || !d.ok) throw new Error(d.error || "Strategy generation failed");
                setSidecarParams(d.best_params);
                setResults(p => ({ ...p, backtest: { engine: d.engine, best_metrics: d.metrics, resolved_params: d.best_params, reconciliation: d.reconciliation, rr_goal: goal } }));
                setViewTab("overview");
              } catch(e) { setError(e.message); }
              finally { stopProgress(); setLoading(false); }
            }} disabled={loading} color={C.purple} style={{ width: "100%", padding: "10px" }}>
              ⚡ GENERATE CANDIDATE ({symbol})
            </Btn>
          </div>

        </div>

        {/* ── RIGHT RESULT DISPLAY ───────────────────────────────────────── */}
        <div style={{ flex: "1 1 320px", minWidth: 0 }}>

          {loading && <ProgressTracker step={loadStep} logs={loadLogs} elapsed={elapsedSec} mode={mode} />}

          {error && !loading && (
            <div style={{ background: `${C.red}15`, border: `1px solid ${C.red}44`, borderRadius: 10, padding: 20, color: C.red, fontWeight: 700, marginBottom: 20 }}>
              ⚠️ Engine Execution Error: {error}
            </div>
          )}

          {!loading && !error && !activeResult && (
            <div style={{ background: C.panel, borderRadius: 10, border: `2px dashed ${C.border}`, padding: "clamp(20px, 6vw, 60px)", textAlign: "center", color: C.muted }}>
              <div style={{ fontSize: 42, marginBottom: 14 }}>📊</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.text, marginBottom: 8 }}>Ready for Simulation</div>
              <div style={{ fontSize: 13, maxWidth: 480, margin: "0 auto", lineHeight: 1.6 }}>
                Select a strategy, configure target risk:reward rules, and run. Explore strategy performance, walk-forward degradation, and Monte Carlo risk profile seamlessly.
              </div>
            </div>
          )}

          {!loading && activeResult && (
            <div style={{ background: C.panel, borderRadius: 10, border: `1px solid ${C.border}`, overflow: "hidden" }}>

              {/* Top Result Banner */}
              {m && (
                <div style={{ padding: "16px 22px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 0.5 }}>
                      {mode.toUpperCase()} · {strat?.replace(".md","")} · {symbol} ({tf}) · {activeResult.engine || "URUS_88"}
                    </div>
                    <div style={{ fontSize: 26, fontWeight: 900, color: wrColor, marginTop: 2 }}>
                      {(m.win_rate * 100).toFixed(1)}% Win Rate
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 16 }}>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 10, color: C.muted }}>NET PROFIT</div>
                      <div style={{ fontSize: 20, fontWeight: 900, color: m.net_profit >= 0 ? C.green : C.red }}>
                        {m.net_profit >= 0 ? "+" : ""}${m.net_profit?.toFixed(2)}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 10, color: C.muted }}>TOTAL TRADES</div>
                      <div style={{ fontSize: 20, fontWeight: 900 }}>{m.total_trades}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 10, color: C.muted }}>PROFIT FACTOR</div>
                      <div style={{ fontSize: 20, fontWeight: 900, color: m.profit_factor >= 1.2 ? C.green : C.amber }}>
                        {m.profit_factor >= 999 ? "∞" : m.profit_factor?.toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab Navigation */}
              <div style={{ display: "flex", borderBottom: `1px solid ${C.border}`, paddingLeft: 8, background: C.bg }}>
                {tabBtn("overview", "Overview & Analytics", !hasOverview)}
                {tabBtn("trades",   "Trade History", !hasOverview)}
                {tabBtn("monthly",  "Monthly Breakdown", !hasOverview)}
                {tabBtn("wfa",      "Walk-Forward Analysis", !results.wfa)}
                {tabBtn("montecarlo","Monte Carlo Robustness", !results.montecarlo)}
                {tabBtn("compare",  "Head-to-Head Compare", !results.compare)}
                {tabBtn("params",   "Parameters & Sidecar", !hasOverview)}
              </div>

              <div style={{ padding: 22 }}>

                {/* OVERVIEW */}
                {viewTab === "overview" && m && (
                  <div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: 10, marginBottom: 18 }}>
                      <StatCard label="Win Rate"      value={(m.win_rate * 100).toFixed(1) + "%"} color={wrColor} size="lg" />
                      <StatCard label="Net Profit"    value={(m.net_profit >= 0 ? "+" : "") + "$" + Math.abs(m.net_profit || 0).toFixed(2)} color={m.net_profit >= 0 ? C.green : C.red} size="lg" />
                      <StatCard label="Profit Factor" value={m.profit_factor >= 999 ? "∞" : m.profit_factor?.toFixed(2) + "x"} color={m.profit_factor >= 1.5 ? C.green : m.profit_factor >= 1 ? C.amber : C.red} size="lg" />
                      <StatCard label="Max Drawdown"  value={(m.max_drawdown * 100).toFixed(1) + "%"} color={m.max_drawdown < 0.1 ? C.green : m.max_drawdown < 0.2 ? C.amber : C.red} size="lg" />
                      <StatCard label="Sharpe"        value={m.sharpe_ratio?.toFixed(3)} color={m.sharpe_ratio > 1 ? C.green : C.amber} size="lg" />
                      <StatCard label="Sortino"       value={m.sortino_ratio?.toFixed(3)} color={m.sortino_ratio > 1.5 ? C.green : C.amber} size="lg" />
                    </div>

                    {/* Equity Curve Chart */}
                    <div style={{ background: C.bg, borderRadius: 8, border: `1px solid ${C.border}`, padding: "14px 16px", marginBottom: 16 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: C.muted, marginBottom: 8 }}>
                        <span style={{ fontWeight: 700, letterSpacing: 0.5 }}>SIMULATED EQUITY TRAJECTORY</span>
                        <span>Start: ${m.initial_capital?.toFixed(2)} → End: <span style={{ color: m.final_capital >= m.initial_capital ? C.green : C.red }}>${m.final_capital?.toFixed(2)}</span></span>
                      </div>
                      <EquityCurve curve={m.equity_curve} initial={m.initial_capital || parseFloat(capital)} height={140} C={C} />
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: 8, marginBottom: 16 }}>
                      <StatCard label="Calmar Ratio"    value={m.calmar_ratio?.toFixed(3)}          color={m.calmar_ratio > 1 ? C.green : C.amber} />
                      <StatCard label="System Quality (SQN)" value={m.sqn?.toFixed(3)}             color={m.sqn > 1.6 ? C.green : C.amber} />
                      <StatCard label="Expectancy"      value={(m.expectancy >= 0 ? "+" : "") + "$" + m.expectancy?.toFixed(4)} color={m.expectancy >= 0 ? C.green : C.red} sub="per trade" />
                      <StatCard label="W:L Ratio"       value={m.avg_win_loss_ratio?.toFixed(2) + "x"} color={m.avg_win_loss_ratio >= 1 ? C.green : C.amber} />
                      <StatCard label="Avg Win"         value={"$" + m.avg_win?.toFixed(4)}         color={C.green} />
                      <StatCard label="Avg Loss"        value={"-$" + m.avg_loss?.toFixed(4)}       color={C.red} />
                      <StatCard label="Win Streak"      value={String(m.max_consecutive_wins || 0)}  color={C.green} />
                      <StatCard label="Loss Streak"     value={String(m.max_consecutive_losses || 0)} color={C.red} />
                    </div>
                  </div>
                )}

                {/* TRADES */}
                {viewTab === "trades" && m && <TradesTable trades={m.trades} />}

                {/* MONTHLY */}
                {viewTab === "monthly" && m && <MonthlyTable monthly={m.monthly_pnl} />}

                {/* WFA */}
                {viewTab === "wfa" && results.wfa && <WFAResults data={results.wfa} />}

                {/* MONTE CARLO */}
                {viewTab === "montecarlo" && results.montecarlo && <MonteCarloResults data={results.montecarlo} />}

                {/* COMPARE */}
                {viewTab === "compare" && results.compare && <CompareResults data={results.compare} />}

                {/* PARAMS */}
                {viewTab === "params" && (activeResult?.best_params || activeResult?.resolved_params) && (
                  <div>
                    {activeResult.best_params && (
                      <div style={{ marginBottom: 20 }}>
                        <div style={{ fontSize: 12, color: C.purple, fontWeight: 700, marginBottom: 12 }}>🧬 CERTIFIED PARAMS (written to sidecar)</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                          {Object.entries(activeResult.best_params).map(([k, v]) => (
                            <div key={k} style={{ background: `${C.purple}11`, border: `1px solid ${C.purple}44`, borderRadius: 8, padding: "12px 16px", minWidth: 100, textAlign: "center" }}>
                              <div style={{ fontSize: 10, color: C.purple, marginBottom: 4, fontWeight: 700 }}>{k.toUpperCase()}</div>
                              <div style={{ fontSize: 20, fontWeight: 900, color: "#fff" }}>{typeof v === "number" ? v.toFixed(3) : v}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {activeResult.resolved_params && (
                      <div style={{ marginTop: 16 }}>
                        <div style={{ fontSize: 12, color: C.blue, fontWeight: 700, marginBottom: 10 }}>PARAMS USED IN THIS RUN</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                          {Object.entries(activeResult.resolved_params).map(([k, v]) => (
                            <span key={k} style={{ background: `${C.blue}11`, border: `1px solid ${C.blue}33`, color: C.blue, padding: "5px 12px", borderRadius: 5, fontSize: 12 }}>
                              {k}: <strong style={{ color: "#fff" }}>{typeof v === "number" ? v.toFixed(3) : v}</strong>
                            </span>
                          ))}
                        </div>
                        <div style={{ marginTop: 10, fontSize: 11, color: C.muted }}>
                          Engine: <span style={{ color: C.cyan }}>{activeResult.engine}</span>
                          {" · "}Strategy: <span style={{ color: C.cyan }}>{strat}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

              </div>
            </div>
          )}

        </div>

      </div>

      <style>{`@keyframes bt_scan { 0%{transform:translateX(-100%)} 100%{transform:translateX(350%)} } @keyframes pulse { 0%{opacity:0.3} 50%{opacity:1} 100%{opacity:0.3} }`}</style>
    </div>
  );
}

